import { AlertCircle, CalendarDays, CheckCircle2, ChevronRight, Clock3, FileText, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { APP_DATE } from '../../data/seed'
import { DRIVER_CONDITION_ITEMS, VEHICLE_INSPECTION_ITEMS, safetyIssueLabels, safetyStatusLabel } from '../../data/safetyChecks'
import { Button, EmptyState, Modal, StatusTag } from '../../components/shared'

export default function Dashboard({ drivers, vehicles, records, onPage, appDate = APP_DATE, onConfirm }) {
  const [selectedDate, setSelectedDate] = useState(appDate)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('all')
  const [method, setMethod] = useState('all')
  const [selected, setSelected] = useState(null)
  const selectedDateRecords = records.filter((record) => record.date === selectedDate && record.phase === '業務前')
  const recordByDriver = Object.fromEntries(selectedDateRecords.map((record) => [record.driverId, record]))
  const selectedDateRows = drivers.filter((driver) => driver.active).map((driver) => {
    const record = recordByDriver[driver.id]
    const vehicle = vehicles.find((item) => item.id === (record?.vehicleId || driver.vehicleIds[0]))
    return { driver, vehicle, record, status: record?.status || 'missing' }
  })
  const counts = {
    completed: selectedDateRows.filter((row) => row.status === 'completed').length,
    missing: selectedDateRows.filter((row) => row.status === 'missing').length,
    review: selectedDateRows.filter((row) => row.status === 'review').length,
  }
  const filtered = useMemo(() => selectedDateRows.filter((row) => {
    const needle = search.toLowerCase()
    const matchesSearch = !needle || `${row.driver.name} ${row.vehicle?.number || ''}`.toLowerCase().includes(needle)
    const matchesStatus = status === 'all' || row.status === status
    const matchesMethod = method === 'all' || row.record?.method === method
    return matchesSearch && matchesStatus && matchesMethod
  }), [selectedDateRows, search, status, method])
  const attention = selectedDateRows.filter((row) => row.status !== 'completed')
  const selectedDateJa = new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'short', timeZone: 'Asia/Tokyo',
  }).format(new Date(`${selectedDate}T00:00:00+09:00`))

  const changeDate = (nextDate) => {
    if (!nextDate) return
    setSelectedDate(nextDate)
    setStatus('all')
    setMethod('all')
    setSelected(null)
  }

  const SummaryItem = ({ type, label, count, icon: Icon }) => (
    <button className={`summary-item summary-item--${type}`} onClick={() => setStatus(type)}>
      <Icon /><span><small>{label}</small><strong>{count}<em>名</em></strong></span>
    </button>
  )

  return (
    <>
      <div className="page-heading"><div><h2>実施状況</h2><p>{selectedDateJa}の業務前チェック</p></div><Button icon={FileText} onClick={() => onPage('report')}>月次報告書を作成</Button></div>
      <div className="dashboard-grid">
        <div className="dashboard-main">
          <section className="summary-band" aria-label={`${selectedDateJa}の集計`}>
            <SummaryItem type="completed" label="完了" count={counts.completed} icon={CheckCircle2} />
            <SummaryItem type="missing" label="未実施" count={counts.missing} icon={Clock3} />
            <SummaryItem type="review" label="要確認" count={counts.review} icon={AlertCircle} />
          </section>
          <div className="filter-bar">
            <label><CalendarDays /><input type="date" value={selectedDate} onInput={(event) => changeDate(event.currentTarget.value)} aria-label="対象日" /></label>
            <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="状態"><option value="all">すべての状態</option><option value="completed">完了</option><option value="missing">未実施</option><option value="review">要確認</option></select>
            <select value={method} onChange={(event) => setMethod(event.target.value)} aria-label="確認方法"><option value="all">すべての記録方法</option><option>画像読取</option><option>手入力</option></select>
            <label className="search-field"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="ドライバー名・車両・ナンバーで検索" /></label>
          </div>
          <div className="table-wrap dashboard-table-wrap">
            <table className="data-table dashboard-table">
              <thead><tr><th>時刻</th><th>ドライバー</th><th>車両</th><th>安全点呼</th><th>測定値</th><th>記録方法</th><th>状態</th><th aria-label="詳細" /></tr></thead>
              <tbody>
                {filtered.slice(0, 12).map((row) => (
                  <tr key={row.driver.id} className={row.status === 'review' ? 'row-review' : ''} onClick={() => row.record && setSelected(row)}>
                    <td>{row.record?.time || '—'}</td><td><strong>{row.driver.name}</strong></td><td>{row.vehicle?.number || '未割当'}</td><td className={row.record?.preDriveCheck?.status === 'review' ? 'safety-cell safety-cell--review' : 'safety-cell'}>{row.record ? safetyStatusLabel(row.record.preDriveCheck) : '—'}</td>
                    <td className="measurement-cell">{row.record ? `${Number(row.record.value).toFixed(2)} mg/L` : '—'}</td>
                    <td>{row.record?.method || '—'}</td><td><StatusTag status={row.status} /></td><td>{row.record ? <ChevronRight /> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!filtered.length ? <EmptyState title="検索条件に一致する記録がありません" detail="状態または検索語を変更してください。" /> : null}
            <div className="table-footer"><span>全{filtered.length}件中 {Math.min(filtered.length, 12)}件を表示</span><Button variant="ghost" onClick={() => onPage('records')}>すべての記録を見る<ChevronRight /></Button></div>
          </div>
        </div>
        <aside className="attention-rail">
          <header><h3>未実施・要確認</h3><span>{attention.length}件</span></header>
          <div>
            {attention.slice(0, 6).map((row) => (
              <button key={row.driver.id} className={`attention-item attention-item--${row.status}`} onClick={() => row.record && setSelected(row)}>
                <span className="attention-item__icon">{row.status === 'review' ? <AlertCircle /> : <Clock3 />}</span>
                <span><strong>{row.driver.name}</strong><small>{row.vehicle?.number || '車両未選択'}</small>{row.record ? <small>測定値: {Number(row.record.value).toFixed(2)} mg/L</small> : null}<StatusTag status={row.status} /></span>
                <ChevronRight />
              </button>
            ))}
          </div>
          <button className="attention-rail__all" onClick={() => { setStatus('missing'); window.scrollTo({ top: 300, behavior: 'smooth' }) }}>すべて表示<ChevronRight /></button>
        </aside>
      </div>
      <Modal open={Boolean(selected)} title="チェック記録の詳細" onClose={() => setSelected(null)} wide>
        {selected ? <RecordDetail row={selected} onConfirm={async (record, details) => { await onConfirm(record, details); setSelected(null) }} /> : null}
      </Modal>
    </>
  )
}

export function RecordDetail({ row, onConfirm }) {
  const { record, driver, vehicle } = row
  const [details, setDetails] = useState({ method: '', visual: '', instruction: '', checked: false })
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const confirm = async () => {
    setBusy(true); setError('')
    try { await onConfirm(record, details) } catch (failure) { setError(failure.message) } finally { setBusy(false) }
  }
  return (
    <div className="record-detail">
      <div className="record-detail__summary">
        <div className={`record-value record-value--${record.status}`}><span>{record.status === 'review' ? '要確認' : '測定値'}</span><strong>{Number(record.value).toFixed(2)}<small>mg/L</small></strong></div>
        {record.imageDataUrl ? <img src={record.imageDataUrl} alt="登録されたチェッカー画像" /> : <div className="record-image-empty">画像なし<span>{record.method}</span></div>}
      </div>
      <dl className="detail-grid">
        <div><dt>運転者</dt><dd>{driver.name}</dd></div><div><dt>車両</dt><dd>{vehicle?.number}</dd></div>
        <div><dt>確認日時</dt><dd>{record.date} {record.time}</dd></div><div><dt>区分</dt><dd>{record.phase}</dd></div>
        <div><dt>確認者</dt><dd>{record.confirmer}</dd></div><div><dt>確認方法</dt><dd>{record.confirmationMethod}</dd></div>
        <div><dt>酒気帯び</dt><dd>{record.confirmationMethod === '未確認' ? '管理者未確認' : record.presence ? '有' : '無'}</dd></div><div><dt>目視等</dt><dd>{record.visualCheck}</dd></div>
        <div><dt>指示事項</dt><dd>{record.instruction}</dd></div><div><dt>備考</dt><dd>{record.note}</dd></div>
      </dl>
      {record.preDriveCheck ? <SafetyCheckDetail check={record.preDriveCheck} /> : null}
      {onConfirm && record.confirmationMethod === '未確認' ? <section className="safety-detail">
        <h3>管理者による確認を記録</h3>
        <p>写真だけでは完了しません。対面・通話等で実際に確認してください。測定値と写真は変更されません。</p>
        <label>確認方法<select aria-label="管理者の確認方法" value={details.method} onChange={event => setDetails({ ...details, method: event.target.value })}><option value="">選択してください</option><option>対面</option><option>ビデオ通話</option><option>電話・音声通話</option></select></label>
        <label>目視等の結果<select aria-label="管理者の目視結果" value={details.visual} onChange={event => setDetails({ ...details, visual: event.target.value })}><option value="">選択してください</option><option>異常なし</option><option>要確認</option></select></label>
        <label>指示事項<textarea aria-label="管理者の指示事項" value={details.instruction} onChange={event => setDetails({ ...details, instruction: event.target.value })} placeholder="指示がなければ「なし」。異常時は運転中止等を記録" /></label>
        <label className="confirm-check"><input type="checkbox" checked={details.checked} onChange={event => setDetails({ ...details, checked: event.target.checked })} />実際に運転者の状態と測定結果を確認しました</label>
        {error ? <p className="inline-error">{error}</p> : null}
        <Button disabled={busy} onClick={confirm}>{busy ? '保存中' : '管理者確認を保存'}</Button>
      </section> : null}
      <section className="audit-log"><h3>監査ログ</h3>{record.audit?.map((entry) => <p key={entry}>{entry}</p>)}</section>
    </div>
  )
}

function SafetyCheckDetail({ check }) {
  const issues = safetyIssueLabels(check)
  return (
    <section className={`safety-detail ${issues.length ? 'safety-detail--review' : ''}`}>
      <header><div><h3>運転前点呼・日常点検</h3><p>酒気帯び確認とは別の運転前安全記録</p></div><strong>{issues.length ? '要確認' : '良好'}</strong></header>
      <div className="safety-detail__groups">
        <div><h4>運転者の状態</h4>{DRIVER_CONDITION_ITEMS.map((item) => <p key={item.id}><span>{item.label}</span><b className={check.driverCondition?.[item.id] === 'review' ? 'review' : ''}>{check.driverCondition?.[item.id] === 'review' ? '要確認' : '問題なし'}</b></p>)}</div>
        <div><h4>車両の日常点検</h4>{VEHICLE_INSPECTION_ITEMS.map((item) => <p key={item.id}><span>{item.label}</span><b className={check.vehicleInspection?.[item.id] === 'review' ? 'review' : ''}>{check.vehicleInspection?.[item.id] === 'review' ? '要確認' : '良好'}</b></p>)}</div>
      </div>
      {check.note ? <p className="safety-detail__note"><strong>異常・連絡事項</strong>{check.note}</p> : null}
    </section>
  )
}
